import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {openErrorModal} from '@Modals/error-modal';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {SOURCE} from '@Libraries/stock-graphic-library';
import {clearSelectedItems, getSelectedGridItems, setGridItems, toggleGridItemSelection} from '@Components/base-grid';
import MasonryGrid from '@Components/masonry-grid';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {AlbumData, FacebookPhotosResponse} from '@Panels/user-media-panel/user-media-panel.types';
import {COOKIE_FB_SESSION_KEY, FACEBOOK_PHOTOS_GRID} from '@Panels/user-media-panel/user-media-panel.types';
import {Text} from '@Components/text';
import {readCookie} from '@Utils/cookie.util';
import {useAppDispatch, useAppSelector} from '@/hooks';

interface FacebookAlbumPhotosProps {
  selectedAlbum: AlbumData;
  maxSelectionNumber?: number;

  onAddSelected(): void;
}

export function FacebookAlbumPhotos({...props}: FacebookAlbumPhotosProps): ReactElement {
  const selectedGridItems = useAppSelector((state) => {
    return getSelectedGridItems(state, FACEBOOK_PHOTOS_GRID);
  });
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    void loadAlbumPhotos();

    return () => {
      dispatch(
        clearSelectedItems({
          gridSelectionGroupId: FACEBOOK_PHOTOS_GRID,
        })
      );
    };
  }, [props.selectedAlbum]);

  const loadAlbumPhotos = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const albumPhotos = (await window.PMW.readLocal('user/getFacebookAlbumPhotosNew', {
        aid: props.selectedAlbum.aid,
        fbSessionKey: readCookie(COOKIE_FB_SESSION_KEY),
      })) as Array<FacebookPhotosResponse>;
      showAlbumPhotos(albumPhotos);
    } catch (e: any) {
      console.error(e);
      openErrorModal();
    }
    setIsLoading(false);
  };

  const showAlbumPhotos = (albumPhotos: Array<FacebookPhotosResponse>): void => {
    const gridItems = [];
    for (const albumPhoto of albumPhotos) {
      gridItems.push({
        type: GRID_ITEM_TYPE.STOCK_IMAGE,
        id: albumPhoto.id,
        thumbSrc: albumPhoto.s,
        source: SOURCE.FACEBOOK,
        width: albumPhoto.width,
        height: albumPhoto.height,
        data: {
          url: albumPhoto.b,
        },
      });
    }

    dispatch(
      setGridItems({
        id: FACEBOOK_PHOTOS_GRID,
        gridSelectionGroupId: FACEBOOK_PHOTOS_GRID,
        items: gridItems,
      })
    );
  };

  const onGridItemClicked = async (gridItem: GridItemStorage, isDoubleClick: boolean): Promise<void> => {
    if (isDoubleClick && selectedGridItems.length && selectedGridItems[0].id === gridItem.id) {
      props.onAddSelected();
      return;
    }

    dispatch(
      toggleGridItemSelection({
        gridItemId: gridItem.id,
        gridSelectionGroupId: FACEBOOK_PHOTOS_GRID,
      })
    );
  };

  if (isLoading) {
    return (
      <div className="_full-width _full-height flex-center flex-v-row">
        <div>
          <CircularProgressLoader size={LOADER_SIZE.LARGE} />
        </div>
        <Text val={window.i18next.t('pmwjs_loading_fb_album')} className="content-body" />
      </div>
    );
  }

  return (
    <MasonryGrid
      gridId={FACEBOOK_PHOTOS_GRID}
      className="spacing-p-l-4 spacing-p-t-9 spacing-p-r-4 spacing-p-b-6 _full-height"
      maxSelectionNumber={props.maxSelectionNumber}
      customOnGridItemClicked={(gridItem: GridItemStorage, isDoubleClick: boolean): void => {
        void onGridItemClicked(gridItem, isDoubleClick);
      }}
    />
  );
}
